'use client'

import { Footer } from '@/components/footer'
import { Header } from '@/components/header'
import { WeekendAnnouncement } from '@/components/home/weekend-announcement'
import { WeekendFaq } from '@/components/weekend/weekend-faq'
import { homepageFaqs } from '@/lib/homepage-faqs'
import { captureAnalyticsEvent } from '@/lib/analytics'
import { getExperienceVisitorFacts } from '@/lib/experience-visitor-facts'
import {
  destinationExperiences,
  getHomepageExperiencePath,
  homepageDestinations,
  homepageExperiences,
  type HomepageDestination,
  type HomepageExperience,
} from '@/lib/homepage-experiences'
import {
  COIMBATORE_EXPERIENCES_URL,
  KANCHEEPURAM_EXPERIENCES_URL,
  THANJAVUR_EXPERIENCES_URL,
} from '@/lib/links'
import { liveDistrictsLabel, openingNextDistricts } from '@/lib/live-districts'
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  Clock3,
  Compass,
  ExternalLink,
  Handshake,
  Hotel,
  Landmark,
  MapPin,
  Pause,
  Plane,
  Play,
  Sprout,
  Store,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useReducedMotion } from 'framer-motion'
import { useEffect, useState } from 'react'

type Audience = {
  icon: LucideIcon
  title: string
  summary: string
  value: string
}

const audiences: Audience[] = [
  {
    icon: Plane,
    title: 'Travellers',
    summary: 'Find meaningful things to do beyond the usual sightseeing list.',
    value: 'Small-group experiences hosted by local people.',
  },
  {
    icon: Hotel,
    title: 'Hotels & resorts',
    summary: 'Give every stay a distinctive sense of place.',
    value: 'A trusted local experience layer for your guests.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Travel partners',
    summary: 'Add authentic, destination-specific moments to itineraries.',
    value: 'Curated inventory with on-ground coordination.',
  },
  {
    icon: Landmark,
    title: 'Government & institutions',
    summary: 'Build responsible tourism around living culture and communities.',
    value: 'Host discovery, programme design and local participation.',
  },
  {
    icon: TrendingUp,
    title: 'Investors',
    summary: 'See how local supply and visitor demand can grow together.',
    value: 'A scalable network built destination by destination.',
  },
]

const modelSteps = [
  {
    number: '01',
    icon: Sprout,
    title: 'Discover',
    body: 'We find local people with genuine knowledge, skill and stories to share.',
  },
  {
    number: '02',
    icon: Handshake,
    title: 'Shape',
    body: 'We turn that knowledge into clear, respectful, visitor-ready experiences.',
  },
  {
    number: '03',
    icon: Compass,
    title: 'Connect',
    body: 'We bring them to travellers, stays, travel partners and destination programmes.',
  },
]

const destinationMarketplaceUrls: Record<HomepageDestination, string> = {
  Thanjavur: THANJAVUR_EXPERIENCES_URL,
  Coimbatore: COIMBATORE_EXPERIENCES_URL,
  Kancheepuram: KANCHEEPURAM_EXPERIENCES_URL,
}

const themeLabels: Record<HomepageExperience['theme'], string> = {
  craft: 'Craft',
  food: 'Food',
  land: 'Land & farms',
  heritage: 'Living heritage',
  music: 'Music & traditions',
}

const heroExperienceSlugs = new Set([
  'thirubuvanam-silk-weaving',
  'kumbakonam-betel-farm-walk',
  'karuppur-kalamkari',
  'neera-tapping',
])

const heroExperiences = homepageExperiences.filter((experience) =>
  heroExperienceSlugs.has(experience.slug),
)

export function Homepage() {
  const [destination, setDestination] = useState<HomepageDestination>('Thanjavur')
  const [heroExperienceIndex, setHeroExperienceIndex] = useState(0)
  const [heroInteractionPaused, setHeroInteractionPaused] = useState(false)
  const [heroRotationMode, setHeroRotationMode] = useState<'auto' | 'paused' | 'playing'>('auto')
  const prefersReducedMotion = useReducedMotion()
  const experiences = destinationExperiences[destination]
  const heroExperience = heroExperiences[heroExperienceIndex]
  const heroRotationEnabled =
    heroRotationMode === 'playing' || (heroRotationMode === 'auto' && !prefersReducedMotion)

  useEffect(() => {
    if (!heroRotationEnabled || heroInteractionPaused || heroExperiences.length < 2) return

    const timer = window.setTimeout(() => {
      setHeroExperienceIndex((current) => (current + 1) % heroExperiences.length)
    }, 5500)

    return () => window.clearTimeout(timer)
  }, [heroExperienceIndex, heroInteractionPaused, heroRotationEnabled])

  const chooseDestination = (nextDestination: HomepageDestination) => {
    captureAnalyticsEvent('destination_selected', { destination: nextDestination })
    setDestination(nextDestination)
  }

  return (
    <div className="oorva-site-theme min-h-screen overflow-x-clip bg-background text-foreground selection:bg-brand selection:text-white">
      <Header
        primaryCtaHref="#experiences"
        primaryCtaLabel="Explore experiences"
        primaryCtaExternal={false}
      />

      <main>
        <div className="bg-background pt-[calc(4.5rem+env(safe-area-inset-top))]">
          <WeekendAnnouncement />
        </div>
        <section className="bg-background pb-10 pt-6 sm:pb-12 sm:pt-8 lg:pb-14">
          <div className="site-shell grid items-center gap-8 lg:grid-cols-[0.96fr_1.04fr] lg:gap-10">
            <div className="lg:py-6">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="size-2 rounded-full bg-brand" aria-hidden="true" />
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-brand">
                  Authentic local experiences
                </p>
                <span className="hidden h-px w-8 bg-brand/30 sm:block" aria-hidden="true" />
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Tamil Nadu
                </p>
              </div>
              <h1 className="mt-5 max-w-[13ch] text-[clamp(3.4rem,5.3vw,5.25rem)] font-semibold leading-[0.93] tracking-[-0.06em]">
                Meet a place through its people.
                <span className="mt-4 block max-w-[28rem] text-lg font-medium leading-8 tracking-[-0.02em] text-muted-foreground sm:text-xl">
                  Book authentic local experiences in Tamil Nadu, India.
                </span>
              </h1>
              <p className="mt-5 max-w-[34rem] text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                Oorva curates bookable <Link href="/experiences" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">cultural experiences in Tamil Nadu</Link>, India —{' '}
                <Link href="/thanjavur" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">artisan workshops in Thanjavur</Link>,{' '}
                <Link href="/weekend-trips" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">farm walks</Link>, traditional food and folk arts hosted by local artisans, farmers, cooks and
                storytellers, so travel feels more personal, grounded and memorable.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a href="#experiences" className="site-button-primary">
                  Find an experience <ArrowRight className="size-4" />
                </a>
                <Link href="/collaborations" className="site-button-secondary">
                  Work with Oorva
                </Link>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 border-t border-border pt-5 sm:gap-4">
                {['Hosted by locals', 'Small groups', 'Oorva curated'].map((value) => (
                  <div key={value} className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-2.5">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
                      <Check className="size-3.5" strokeWidth={2.4} />
                    </span>
                    <p className="text-xs font-semibold leading-5 tracking-[-0.01em] text-foreground sm:text-sm">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div
              role="region"
              aria-roledescription="carousel"
              aria-label="Featured Oorva experiences"
              className="relative min-h-[400px] overflow-hidden rounded-[1.75rem] bg-primary sm:min-h-[500px] lg:min-h-[620px]"
              onMouseEnter={() => setHeroInteractionPaused(true)}
              onMouseLeave={() => setHeroInteractionPaused(false)}
              onFocusCapture={() => setHeroInteractionPaused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setHeroInteractionPaused(false)
                }
              }}
              onTouchStart={(event) => {
                const x = event.touches[0]?.clientX ?? null
                event.currentTarget.dataset.swipeX = x == null ? '' : String(x)
              }}
              onTouchEnd={(event) => {
                const start = Number(event.currentTarget.dataset.swipeX)
                const end = event.changedTouches[0]?.clientX
                event.currentTarget.dataset.swipeX = ''
                if (!Number.isFinite(start) || end == null) return
                const dx = end - start
                if (Math.abs(dx) > 40) {
                  setHeroInteractionPaused(true)
                  setHeroExperienceIndex((current) =>
                    dx < 0
                      ? (current + 1) % heroExperiences.length
                      : (current - 1 + heroExperiences.length) % heroExperiences.length,
                  )
                }
              }}
            >
              <Link
                href={getHomepageExperiencePath(heroExperience.slug)}
                aria-label={`View ${heroExperience.title}`}
                data-analytics-event="experience_selected"
                data-analytics-experience-slug={heroExperience.slug}
                data-analytics-experience-name={heroExperience.title.replace('\n', ' ')}
                data-analytics-destination={heroExperience.district}
                data-analytics-theme={heroExperience.theme}
                data-analytics-selection-source="homepage_hero"
                className="group absolute inset-0 block"
              >
                {heroExperiences.map((experience, index) => {
                  const isActive = index === heroExperienceIndex
                  // Only mount the active slide — stacked <Image fill> otherwise
                  // downloads all 4 hero candidates on every homepage visit.
                  if (!isActive) return null

                  return (
                    <div
                      key={experience.slug}
                      aria-hidden={false}
                      className="absolute inset-0 scale-100 opacity-100 transition-[opacity,transform] duration-1000 ease-out motion-reduce:transition-none"
                    >
                      <Image
                        src={experience.image}
                        alt={`${experience.title} in ${experience.location}`}
                        fill
                        priority={index === 0}
                        loading={index === 0 ? undefined : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : 'low'}
                        quality={75}
                        className="image-drift object-cover"
                        sizes="(min-width: 1024px) 56vw, 100vw"
                      />
                    </div>
                  )
                })}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/5 to-primary/15" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 text-white sm:p-8">
                  <div>
                    <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-white/65">
                      {heroExperience.location} · {heroExperience.district}
                    </p>
                    <p className="mt-2 max-w-sm text-xl font-semibold leading-tight sm:text-2xl">
                      {heroExperience.title}
                    </p>
                  </div>
                  <span className="grid size-12 shrink-0 place-items-center rounded-full border border-white/25 bg-white/10 backdrop-blur transition group-hover:bg-white group-hover:text-primary">
                    <ArrowRight className="size-4" />
                  </span>
                </div>
              </Link>

              <div className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-full border border-white/20 bg-primary/35 p-1.5 text-white backdrop-blur-md sm:right-6 sm:top-6">
                {heroExperiences.map((experience, index) => (
                  <button
                    key={experience.slug}
                    type="button"
                    aria-label={`Show ${experience.title}`}
                    aria-current={index === heroExperienceIndex ? 'true' : undefined}
                    onClick={() => setHeroExperienceIndex(index)}
                    className="grid size-11 place-items-center rounded-full hover:bg-white/10"
                  >
                    <span
                      className={`h-2 rounded-full transition-[width,background-color] motion-reduce:transition-none ${
                        index === heroExperienceIndex ? 'w-4 bg-white' : 'w-2 bg-white/45'
                      }`}
                    />
                  </button>
                ))}
                <span className="mx-0.5 h-4 w-px bg-white/25" aria-hidden="true" />
                <button
                  type="button"
                  aria-label={heroRotationEnabled ? 'Pause featured experiences' : 'Play featured experiences'}
                  onClick={() => setHeroRotationMode(heroRotationEnabled ? 'paused' : 'playing')}
                  className="grid size-11 place-items-center rounded-full transition hover:bg-white/15"
                >
                  {heroRotationEnabled ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="what-is-oorva" className="border-y border-border bg-card">
          <div className="site-shell site-section">
            <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12">
              <div>
                <p className="site-label">Oorva, simply</p>
                <p className="mt-4 max-w-sm text-base leading-7 text-muted-foreground">
                  A local experience company connecting curious travellers and tourism partners
                  with the people who give a destination its character.
                </p>
              </div>
              <div>
                <h2 className="site-title max-w-[20ch]">Not another tour — authentic cultural experiences hosted by locals in Tamil Nadu.</h2>
                <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
                  We make local knowledge easier to discover, understand, trust and book — from{' '}
                  <Link href="/experience/thirubhuvanam-silk-weaving" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">Thanjavur silk weaving</Link> and{' '}
                  <Link href="/experience/thanjavur-doll-making" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">doll making</Link> to{' '}
                  <Link href="/experience/kumbakonam-betel-leaf-farm" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">farm walks</Link> and home-style Tamil meals.
                </p>
              </div>
            </div>

            <div className="mt-10 grid border-y border-border lg:grid-cols-3">
              {modelSteps.map(({ number, icon: Icon, title, body }, index) => (
                <article
                  key={number}
                  className={`grid grid-cols-[auto_1fr] gap-4 py-6 lg:block lg:px-7 lg:py-7 ${index > 0 ? 'border-t border-border lg:border-l lg:border-t-0' : ''} lg:first:pl-0 lg:last:pr-0`}
                >
                  <span className="grid size-10 place-items-center rounded-full bg-brand-soft text-brand">
                    <Icon className="size-4" />
                  </span>
                  <div className="lg:mt-4">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-xl font-semibold tracking-[-0.025em]">{title}</h3>
                      <span className="text-xs font-bold text-foreground/25">{number}</span>
                    </div>
                    <p className="mt-2 max-w-sm text-sm leading-6 text-muted-foreground">{body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="experiences" className="bg-background">
          <div className="site-shell site-section">
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <p className="site-label">Curated for the curious</p>
                <h2 className="site-title mt-4">Start with a place: artisan workshops, farm walks and food experiences in Thanjavur, Kancheepuram and Coimbatore.</h2>
                <p className="site-copy mt-5 max-w-xl">
                  Step into workshops, kitchens, farms and homes across <Link href="/thanjavur" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">Thanjavur</Link>, <Link href="/destinations" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">Kancheepuram and Coimbatore</Link>. Leave with a story that belongs
                  only to that destination. Browse all <Link href="/experiences" className="font-semibold text-foreground underline decoration-brand/40 underline-offset-4 hover:decoration-brand">authentic local experiences in India</Link>.
                </p>
              </div>
              <div className="flex max-w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" aria-label="Choose a destination">
                {homepageDestinations.map((item) => {
                  const selected = item === destination

                  return (
                    <button
                      key={item}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => chooseDestination(item)}
                      className={selected
                        ? 'shrink-0 rounded-full bg-primary px-5 py-3 text-xs font-semibold text-primary-foreground'
                        : 'shrink-0 rounded-full border border-border bg-card px-5 py-3 text-xs font-semibold text-muted-foreground transition hover:text-foreground'}
                    >
                      {item}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="-mx-5 mt-8 grid snap-x snap-mandatory grid-flow-col gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] md:mx-0 md:grid-flow-row md:grid-cols-2 md:gap-5 md:overflow-visible md:px-0 md:pb-0 xl:grid-cols-3">
              {experiences.slice(0, 3).map((experience) => (
                <ExperienceCard key={experience.slug} experience={experience} />
              ))}
            </div>

            <div className="mt-6 flex flex-col items-start justify-between gap-3 border-t border-border pt-5 sm:flex-row sm:items-center">
              <p className="text-sm text-muted-foreground">{experiences.length} local experiences in {destination}</p>
              <a
                href={destinationMarketplaceUrls[destination]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold transition hover:text-brand"
              >
                {openingNextDistricts.includes(destination) ? `Preview upcoming in ${destination}` : `Explore all in ${destination}`} <ExternalLink className="size-4" />
              </a>
            </div>
          </div>
        </section>

        <section id="who-it-is-for" className="border-y border-border bg-card">
          <div className="site-shell site-section">
            <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
              <div>
                <p className="site-label">Why travellers trust Oorva</p>
                <h2 className="site-title mt-4 max-w-[16ch]">Small-group local experiences in Tamil Nadu with verified hosts.</h2>
              </div>
              <p className="site-copy max-w-2xl lg:justify-self-end">
                Every experience is hosted by a verified local, capped at a small group size,
                and confirmed with you on WhatsApp before you pay anything. If plans change,
                you talk to a real Oorva team member, not a booking engine.
              </p>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
              <a href="#experiences" className="group relative block min-h-[400px] overflow-hidden rounded-[1.5rem] bg-primary sm:min-h-[500px]">
                <Image
                  src="/weekend-trips/betel-farm-walk.webp"
                  alt="Travellers walking the betel farm with a local farmer"
                  fill
                  className="image-lift object-cover"
                  sizes="(min-width: 1024px) 52vw, 100vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/18 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-7">
                  <span className="inline-flex items-center gap-2 text-[0.66rem] font-bold uppercase tracking-[0.16em] text-white/65">
                    <Plane className="size-3.5" /> For travellers
                  </span>
                  <h3 className="mt-3 max-w-[12ch] text-3xl font-semibold leading-none tracking-[-0.04em] sm:text-5xl">
                    Come curious. Leave connected.
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-6 text-white/68">{audiences[0].summary} {audiences[0].value}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">
                    Find your experience <ArrowRight className="size-4" />
                  </span>
                </div>
              </a>

              <div className="grid gap-4 sm:grid-cols-2">
                {audiences.slice(1).map(({ icon: Icon, title, summary, value }) => (
                  <article key={title} className="rounded-[1.5rem] border border-border bg-background p-5 sm:p-6">
                    <span className="grid size-10 place-items-center rounded-full bg-brand-soft text-brand">
                      <Icon className="size-4" />
                    </span>
                    <h3 className="mt-4 text-xl font-semibold tracking-[-0.025em]">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{summary}</p>
                    <p className="mt-3 text-xs font-semibold leading-5 text-foreground/70">{value}</p>
                  </article>
                ))}
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link href="/collaborations" className="site-button-secondary">
                Explore partnerships <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="site-shell site-section grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-12">
            <div>
              <p className="site-label text-brand-highlight">Better for the journey and the place</p>
              <h2 className="mt-4 max-w-[14ch] text-[clamp(2.7rem,5vw,4.8rem)] font-semibold leading-[0.96] tracking-[-0.05em]">
                Local stories in Tamil Nadu deserve a place in cultural travel.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-8 text-white/62">
                Travellers gain a more meaningful journey. Local hosts gain visibility for their
                knowledge and skill. Partners gain <Link href="/collaborations" className="font-semibold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white">experiences that belong to the destination</Link>.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link href="/collaborations" className="site-button-primary">
                  Build with Oorva <ArrowRight className="size-4" />
                </Link>
                <Link href="/host" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-6 text-sm font-semibold transition hover:bg-white/8">
                  Become a host <Store className="size-4" />
                </Link>
              </div>
              <p className="mt-5 flex items-start gap-2 text-xs leading-6 text-white/45">
                <Check className="mt-1 size-3.5 shrink-0 text-brand-highlight" />
                Live in {liveDistrictsLabel}. {openingNextDistricts[0]} is opening next.
              </p>
            </div>

            <div className="grid grid-cols-[1.08fr_0.92fr] gap-4">
              <div className="relative min-h-[360px] overflow-hidden rounded-[1.5rem] sm:min-h-[500px]">
                <Image src="/thanjavur-artisan-handcraft-workshop.jpg" alt="Thanjavur artisan shaping a traditional handcraft in a local workshop — Oorva authentic experience" fill loading="lazy" quality={70} className="image-scroll object-cover" sizes="(min-width: 1024px) 30vw, 55vw" />
              </div>
              <div className="grid gap-4 pt-8 sm:pt-12">
                <div className="relative min-h-[190px] overflow-hidden rounded-[1.5rem] sm:min-h-[290px]">
                  <Image src="/tamil-nadu-farm-walk-local-host.jpeg" alt="Travellers on a Tamil Nadu farm walk with a local host — Oorva authentic experience" fill loading="lazy" quality={70} className="image-scroll object-cover" sizes="(min-width: 1024px) 25vw, 45vw" />
                </div>
                <div className="rounded-[1.5rem] border border-white/12 bg-white/[0.055] p-5 sm:p-6">
                  <MapPin className="size-5 text-brand-highlight" />
                  <p className="mt-4 text-base font-semibold leading-snug sm:text-xl">Every place has a story worth entering.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-label="Explore destinations and experiences" className="border-t border-border bg-background">
          <div className="site-shell site-section">
            <p className="site-label">Explore by destination</p>
            <h2 className="site-title mt-4 max-w-[20ch]">Authentic things to do in Thanjavur, Kancheepuram and Coimbatore.</h2>
            <p className="site-copy mt-4 max-w-2xl">
              Every Oorva experience is hosted by verified locals. Start from a destination hub or jump straight into a craft, farm or food experience.
            </p>
            {/* Always server-rendered crawlable links — the tab grid above only renders the selected destination (Thanjavur by default), so search crawlers without JS would miss Coimbatore / Kancheepuram URLs without this nav. */}
            <nav aria-label="Destinations and popular experiences" className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <ul className="grid gap-3">
                <li><Link href="/thanjavur" className="group flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 text-sm font-semibold hover:border-brand">Things to do in Thanjavur <ArrowRight className="size-4 transition group-hover:translate-x-1" /></Link></li>
                <li><Link href="/destinations" className="group flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 text-sm font-semibold hover:border-brand">All destinations: Kancheepuram and Coimbatore <ArrowRight className="size-4 transition group-hover:translate-x-1" /></Link></li>
                <li><Link href="/experiences" className="group flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 text-sm font-semibold hover:border-brand">Browse all authentic local experiences in India <ArrowRight className="size-4 transition group-hover:translate-x-1" /></Link></li>
                <li><Link href="/weekend-trips" className="group flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 text-sm font-semibold hover:border-brand">Weekend in Thanjavur — every Sat &amp; Sun <ArrowRight className="size-4 transition group-hover:translate-x-1" /></Link></li>
              </ul>
              <ul className="grid gap-2 sm:grid-cols-2">
                {homepageExperiences.slice(0, 6).map((exp) => (
                  <li key={exp.slug}>
                    <Link
                      href={getHomepageExperiencePath(exp.slug)}
                      className="block rounded-2xl border border-border bg-card px-4 py-3 text-sm leading-6 text-muted-foreground transition hover:border-brand hover:text-foreground"
                    >
                      <span className="block text-[0.62rem] font-bold uppercase tracking-[0.14em] text-brand">{exp.location} · {exp.district}</span>
                      <span className="mt-1 block font-semibold text-foreground">{exp.title.replace('\n', ' ')}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <p className="mt-6 text-xs text-muted-foreground">Updated <time dateTime="2026-09-28">28 September 2026</time> · Live in {liveDistrictsLabel}.</p>
          </div>
        </section>

        <section className="border-t border-border bg-card">
          <div className="site-shell site-section mx-auto max-w-3xl">
            <p className="site-label text-center">Questions</p>
            <h2 className="site-title mt-4 text-center">Booking an authentic local experience in India, answered</h2>
            <div className="mt-8">
              <WeekendFaq items={homepageFaqs} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

function ExperienceCard({ experience }: { experience: HomepageExperience }) {
  const facts = getExperienceVisitorFacts(experience)
  return (
    <article className="travel-card group w-[84vw] max-w-[360px] snap-start overflow-hidden rounded-[1.5rem] border border-border bg-card md:w-auto md:max-w-none">
      <Link
        href={getHomepageExperiencePath(experience.slug)}
        data-analytics-event="experience_selected"
        data-analytics-experience-slug={experience.slug}
        data-analytics-experience-name={experience.title.replace('\n', ' ')}
        data-analytics-destination={experience.district}
        data-analytics-theme={experience.theme}
        data-analytics-selection-source="homepage_grid"
        className="block h-full"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
          <Image
            src={experience.image}
            alt={experience.title}
            fill
            className="image-lift object-cover"
            sizes="(min-width: 1280px) 31vw, (min-width: 768px) 48vw, 100vw"
          />
          <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-primary/70 px-3 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-md">
            {facts.live ? themeLabels[experience.theme] : 'Opening next'}
          </span>
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.66rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5 text-brand" />{experience.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock3 className="size-3.5 text-brand" />{facts.duration}</span>
          </div>
          <h3 className="mt-4 max-w-[18ch] text-2xl font-semibold leading-[1.05] tracking-[-0.035em]">{experience.title}</h3>
          <p className="mt-3 line-clamp-2 text-sm leading-6 text-muted-foreground">{experience.oneLine}</p>
          <p className="mt-3 text-xs leading-5 text-muted-foreground">{facts.price} · {facts.groupSize}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">
            {facts.live ? 'View experience' : 'Preview experience'} <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </article>
  )
}
